import {
	IconExternalLink,
	IconListCheck,
	IconRefresh,
	IconTrash,
} from "@tabler/icons-react";
import {
	Alert,
	Button,
	Flex,
	InputNumber,
	Select,
	Space,
	Switch,
	Tooltip,
	Typography,
} from "antd";
import { formatDistanceToNow } from "date-fns";
import { useEffect, useState } from "react";
import {
	useDeleteSignal,
	useSyncSignalStatus,
	useUpdateSignal,
} from "@/common/artemis-operations/signals";
import type { SignalThresholdsInput } from "@/common/artemis-sdk/graphql";
import { confirmDestructive } from "@/common/confirm";
import {
	DELETE_RESOURCE_ERROR_FAILED,
	DELETE_RESOURCE_INFO_COMPLETED,
	SIGNAL_DELETE_DESC,
	SIGNAL_DELETE_TITLE,
	UPDATE_RESOURCE_ERROR_FAILED,
	UPDATE_RESOURCE_INFO_COMPLETED,
} from "@/common/messages";
import {
	type Deployment,
	type Signal,
	SignalKind,
	type SignalState,
	type SignalStatus,
	ThresholdDirection,
} from "@/common/model";
import { useNotify } from "@/common/use-notify";
import { toLabel } from "@/common/utils";
import { BaseContainer, CustomIcon, IdField } from "@/components/base";
import { BaseDetails, EditableText } from "@/components/base-details";
import { CustomDrawer } from "@/components/layout/workspace/custom-drawer";
import { useWizards } from "@/contexts/wizard-context";
import { SignalSourceLabelMap, SignalStateBadge } from "./signal-badges";
import { formatDelta, formatMetric } from "./signal-format";
import { Sparkline } from "./signal-key-numbers";

/** A task, prefilled with the signal and its deployment, to look into what it is reporting. */
function investigationTask(signal: Signal, status: SignalStatus | undefined) {
	const state = status
		? status.state.toLowerCase().replace("_", " ")
		: "in an unknown state";
	const lines = [
		signal.kind === SignalKind.METRIC && status?.value != null
			? `The metric "${signal.name}" is at ${formatMetric(status.value, status.unit)} (${state}).`
			: `The signal "${signal.name}" is ${state}.`,
		`Source: ${SignalSourceLabelMap[signal.source]} (${signal.externalId}).`,
		status?.reason && `Reason reported by the source: ${status.reason}`,
		status?.error && `Artemis could not read it: ${status.error}`,
		"Find the cause, explain the impact, and propose a fix.",
	];
	return {
		name: `Investigate ${signal.name}`,
		description: lines.filter(Boolean).join("\n\n"),
		projectId: signal.projectId,
		deploymentIds: signal.deploymentId ? [signal.deploymentId] : [],
		environmentIds: signal.environmentId ? [signal.environmentId] : [],
	};
}

/**
 * A metric's warning and critical levels. Saving with both empty removes them, which makes the
 * metric informational again.
 */
function ThresholdsEditor({
	signal,
	saving,
	onSave,
}: {
	signal: Signal;
	saving: boolean;
	onSave: (thresholds: SignalThresholdsInput) => void;
}) {
	const [warning, setWarning] = useState<number | null>(null);
	const [critical, setCritical] = useState<number | null>(null);
	const [direction, setDirection] = useState<ThresholdDirection>(
		ThresholdDirection.ABOVE,
	);
	useEffect(() => {
		setWarning(signal.thresholds?.warning ?? null);
		setCritical(signal.thresholds?.critical ?? null);
		setDirection(signal.thresholds?.direction ?? ThresholdDirection.ABOVE);
	}, [signal.thresholds]);

	return (
		<Flex vertical gap={8}>
			<Typography.Paragraph style={{ margin: 0 }}>
				{signal.thresholds
					? "The metric's state comes from these levels."
					: "None yet: the metric is shown as a number and doesn't count towards the project's health."}
			</Typography.Paragraph>
			<Flex gap={8} wrap align="center">
				<InputNumber
					placeholder="Warning at"
					prefix="Warning"
					value={warning}
					onChange={setWarning}
					style={{ flex: "1 1 110px" }}
				/>
				<InputNumber
					placeholder="Critical at"
					prefix="Critical"
					value={critical}
					onChange={setCritical}
					style={{ flex: "1 1 110px" }}
				/>
				<Select
					value={direction}
					onChange={setDirection}
					style={{ flex: "1 1 130px" }}
					options={[
						{ value: ThresholdDirection.ABOVE, label: "Bad at or above" },
						{ value: ThresholdDirection.BELOW, label: "Bad at or below" },
					]}
				/>
				<Button
					loading={saving}
					onClick={() => onSave({ warning, critical, direction })}
				>
					Save
				</Button>
			</Flex>
		</Flex>
	);
}

/** Viewer drawer for one signal: its live state, where it comes from, and what to do about it. */
export function SignalViewerDrawer({
	signal,
	status,
	deployments,
	open,
	onClose,
}: {
	signal: Signal | null;
	status?: SignalStatus;
	deployments: Deployment[];
	open: boolean;
	onClose: () => void;
}) {
	const notify = useNotify();
	const { openTaskCreateWizard } = useWizards();
	const updateSignal = useUpdateSignal(signal?.projectId ?? "");
	const deleteSignal = useDeleteSignal(signal?.projectId ?? "");
	const syncStatus = useSyncSignalStatus(signal?.projectId ?? "");

	if (!signal) return null;

	const deployment = deployments.find((d) => d.id === signal.deploymentId);

	const isMetric = signal.kind === SignalKind.METRIC;
	const save = (input: {
		name?: string;
		description?: string;
		thresholds?: SignalThresholdsInput;
		keyMetric?: boolean;
	}) =>
		updateSignal.mutate(
			{ signalId: signal.id, input },
			{
				onSuccess: () => notify.success(UPDATE_RESOURCE_INFO_COMPLETED),
				onError: (err) =>
					notify.error((err as Error).message || UPDATE_RESOURCE_ERROR_FAILED),
			},
		);

	const remove = () =>
		confirmDestructive({
			title: SIGNAL_DELETE_TITLE,
			content: SIGNAL_DELETE_DESC,
			onOk: () =>
				deleteSignal.mutate(signal.id, {
					onSuccess: () => {
						notify.success(DELETE_RESOURCE_INFO_COMPLETED);
						onClose();
					},
					onError: (err) =>
						notify.error(
							(err as Error).message || DELETE_RESOURCE_ERROR_FAILED,
						),
				}),
		});

	return (
		<CustomDrawer
			title={signal.name}
			open={open}
			onClose={onClose}
			footer={
				<Flex justify="flex-end">
					<Button
						type="primary"
						icon={<CustomIcon icon={IconListCheck} size={16} />}
						onClick={() =>
							openTaskCreateWizard(investigationTask(signal, status))
						}
					>
						Investigate with a task
					</Button>
				</Flex>
			}
			extra={
				<Space>
					<Tooltip title="Sync now: re-read the source, skipping the cache">
						<Button
							type="text"
							icon={<CustomIcon icon={IconRefresh} />}
							loading={syncStatus.isPending}
							onClick={() =>
								syncStatus.mutate(signal.id, {
									onError: (err) =>
										notify.error(
											(err as Error).message || UPDATE_RESOURCE_ERROR_FAILED,
										),
								})
							}
						/>
					</Tooltip>
					{status?.url && (
						<Tooltip title={`Open in ${SignalSourceLabelMap[signal.source]}`}>
							<Button
								type="text"
								icon={<CustomIcon icon={IconExternalLink} />}
								onClick={() => window.open(status.url as string, "_blank")}
							/>
						</Tooltip>
					)}
					<Tooltip title="Remove signal">
						<Button
							danger
							type="text"
							icon={<CustomIcon icon={IconTrash} />}
							onClick={remove}
						/>
					</Tooltip>
				</Space>
			}
		>
			<Space vertical style={{ width: "100%" }} size="large">
				{status?.error && (
					<Alert
						type="warning"
						title="Status unavailable"
						description={status.error}
					/>
				)}

				<BaseContainer size="small" title="Current state">
					<BaseDetails
						item={{ ...signal, status }}
						fields={[
							{
								key: "status",
								label: "State",
								render: (s) => (
									<SignalStateBadge state={s?.state as SignalState} />
								),
							},
							{
								key: "status",
								label: "Value",
								hidden: () => !isMetric,
								render: (s) => (
									<Typography.Text strong>
										{formatMetric(s?.value, s?.unit)}
									</Typography.Text>
								),
							},
							{
								key: "status",
								label: "Compared with",
								hidden: (s) => !isMetric || s?.previousValue == null,
								render: (s) =>
									`${s?.previousLabel ?? "previous"}: ${formatMetric(s?.previousValue, s?.unit)}${
										formatDelta(s?.value, s?.previousValue)
											? ` (${formatDelta(s?.value, s?.previousValue)})`
											: ""
									}`,
							},
							{
								key: "status",
								label: "Trend",
								hidden: (s) => !isMetric || (s?.series?.length ?? 0) < 2,
								render: (s) => (
									<Sparkline
										series={(s?.series as number[] | null) ?? []}
										unit={s?.unit}
										label={signal.name}
									/>
								),
							},
							{
								key: "status",
								label: "Data as of",
								hidden: (s) => !s?.asOf,
								render: (s) =>
									formatDistanceToNow(new Date(s?.asOf as string), {
										addSuffix: true,
									}),
							},
							{
								key: "status",
								label: "Reason",
								hidden: (s) => !s?.reason,
								render: (s) => <Typography.Text>{s?.reason}</Typography.Text>,
							},
							{
								key: "status",
								label: "Checked",
								hidden: (s) => !s,
								render: (s) =>
									formatDistanceToNow(new Date(s?.observedAt as string), {
										addSuffix: true,
									}),
							},
						]}
					/>
				</BaseContainer>

				{isMetric && (
					<BaseContainer size="small" title="Thresholds">
						<ThresholdsEditor
							signal={signal}
							saving={updateSignal.isPending}
							onSave={(thresholds) => save({ thresholds })}
						/>
					</BaseContainer>
				)}

				<BaseContainer size="small" title="Details">
					<BaseDetails
						item={signal}
						fields={[
							{
								key: "name",
								label: "Name",
								render: (name) => (
									<EditableText onChange={(v) => save({ name: v })}>
										{name}
									</EditableText>
								),
							},
							{
								key: "description",
								label: "Description",
								render: (description) => (
									<EditableText onChange={(v) => save({ description: v })}>
										{description ?? ""}
									</EditableText>
								),
							},
							{ key: "kind", label: "Kind", render: (k) => toLabel(k) },
							{
								key: "keyMetric",
								label: "Key number",
								hidden: () => !isMetric,
								render: (on) => (
									<Switch
										checked={on}
										onChange={(v) => save({ keyMetric: v })}
										disabled={updateSignal.isPending}
									/>
								),
							},
							{
								key: "source",
								label: "Source",
								render: (s) => SignalSourceLabelMap[s],
							},
							{
								key: "deploymentId",
								label: "Scope",
								render: () => deployment?.name ?? "project",
							},
							{
								key: "config",
								label: "Settings",
								hidden: (c) => !c?.length,
								render: (c) =>
									c
										?.filter((kv) => kv.key !== "region")
										.map((kv) => `${kv.key}: ${kv.value}`)
										.join(" · "),
							},
							{
								key: "externalId",
								label: "External ID",
								render: (id) => <IdField id={id} showCopyButton />,
							},
							{
								key: "id",
								label: "Signal ID",
								render: (id) => <IdField id={id} showCopyButton />,
							},
						]}
					/>
				</BaseContainer>
			</Space>
		</CustomDrawer>
	);
}
